import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { TranslationKey } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { useBodyScrollLock, useEscapeKey, useFocusTrap } from '@/lib/dom';
import { cn } from '@/components/ui';
import { Icon, type IconName } from './Icon';

/**
 * The left navigation rail (PRD §28), and the drawer it becomes below 720px.
 *
 * Both render the *same* list from one definition — a separate mobile menu is how the two
 * fall out of step, and on a news site the menu is the map of the whole publication.
 */
interface NavEntry {
  key: TranslationKey;
  icon: IconName;
  /** Path segments after the locale. Empty means the homepage. */
  segments: string[];
}

/**
 * Two slugs here are not what they look like (ADR-005): the "Local" category is seeded as
 * `assam` and "Health" as `healthcare`, because `local` and `health` are reserved words.
 * The rail's LOCAL entry deliberately points at `/local` — the district hub, which is the
 * reserved route — rather than at the category.
 */
const NAV: NavEntry[] = [
  { key: 'nav.home', icon: 'home', segments: [] },
  { key: 'nav.state', icon: 'state', segments: ['state'] },
  { key: 'nav.national', icon: 'national', segments: ['national'] },
  { key: 'nav.local', icon: 'local', segments: ['local'] },
  { key: 'nav.sports', icon: 'sports', segments: ['sports'] },
  { key: 'nav.health', icon: 'health', segments: ['healthcare'] },
  { key: 'nav.jobs', icon: 'jobs', segments: ['jobs'] },
  { key: 'nav.entertainment', icon: 'entertainment', segments: ['entertainment'] },
  { key: 'nav.videos', icon: 'videos', segments: ['videos'] },
];

const SOCIALS: { name: IconName; href: string; label: string }[] = [
  { name: 'facebook', href: 'https://facebook.com/', label: 'Facebook' },
  { name: 'x', href: 'https://x.com/', label: 'X' },
  { name: 'instagram', href: 'https://instagram.com/', label: 'Instagram' },
  { name: 'youtube', href: 'https://youtube.com/', label: 'YouTube' },
];

/**
 * The masthead, identical in the rail and the drawer.
 *
 * The supplied identity, not a typographic imitation of it: the lockup already carries
 * the wordmark and the Assamese tagline, so the markup is one image with the brand name
 * as its alt text rather than three spans trying to reproduce a logo in Playfair.
 *
 * `width`/`height` carry the artwork's own 220×153, so the rail reserves the right space
 * before the image arrives — the masthead is at the top of every page, and a logo that
 * pushes the navigation down on load is layout shift where a reader is most likely to be
 * aiming.
 *
 * Sized by **width**, never by height. The rail is 118px wide and `px-4` leaves 86px
 * inside it; a height of 76px asks this lockup for 109px, and preflight's
 * `img { max-width: 100% }` then clamps the width alone — one dimension capped, the other
 * fixed, so the artwork rendered 85×76 and the wordmark was squeezed 22% narrower than it
 * was drawn. Giving it a width and `h-auto` cannot distort it, whatever the rail's width
 * becomes in another locale.
 *
 * The `max-w` is short of the full 86px on purpose: the lockup's own artwork is trimmed
 * to its ink (`scripts/build-brand-assets.mjs`), so it carries no side bearing of its
 * own, and at the full content width the wordmark runs edge to edge between the rail's
 * borders.
 */
export function Brand({ compact = false }: { compact?: boolean }) {
  const { t, path } = useLocale();
  return (
    <NavLink to={path()} end className="flex flex-col items-center text-center">
      <picture className="block w-full">
        <source srcSet="/brand/lockup-220.webp 1x, /brand/lockup-440.webp 2x" type="image/webp" />
        <img
          src="/brand/lockup-220.png"
          srcSet="/brand/lockup-220.png 1x, /brand/lockup-440.png 2x"
          alt={`${t('brand.mark')} ${t('brand.name')}`}
          width={220}
          height={153}
          // The one image above the fold on every page that is not the story's own.
          fetchPriority="high"
          decoding="async"
          // 70px is what the drawer's old `h-12 w-auto` already resolved to — it has
          // room to spare, so it was never the one being squeezed, and this keeps it
          // where it was.
          className={cn('mx-auto h-auto w-full', compact ? 'max-w-[70px]' : 'max-w-[74px]')}
        />
      </picture>
    </NavLink>
  );
}

/**
 * The rail stacks its icon above the label; the drawer sets them side by side.
 *
 * Not a style preference. The rail is 118px wide — 142px in Assamese — which leaves about
 * 59px beside a 16px icon, and ENTERTAINMENT needs twice that at the design's 12.5px. The
 * supplied HTML simply lets it overflow and clip. Stacking gives the label the rail's full
 * width, so every item stays readable at both widths and in both scripts; the drawer has
 * room for the original horizontal form and keeps it.
 */
function NavList({ layout, onNavigate }: { layout: 'rail' | 'drawer'; onNavigate?: () => void }) {
  const { t, path } = useLocale();
  const rail = layout === 'rail';

  return (
    <ul className={cn('flex flex-col', rail ? 'gap-1' : 'gap-0.5')}>
      {NAV.map((entry) => (
        <li key={entry.key}>
          <NavLink
            to={path(...entry.segments)}
            end={entry.segments.length === 0}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex rounded-md font-semibold transition',
                rail
                  ? 'flex-col items-center gap-1.5 px-1 py-2.5 text-center text-[10.5px] leading-tight tracking-wide'
                  : 'items-center gap-3 px-2.5 py-2.5 text-base',
                // `text-red-dark` on the tint, not `text-red`: the brand red is 3.97:1
                // on its own tint and the darker tone is 5.03:1 (Phase 17,
                // `npm run check:contrast`). On white the brand red stays as it is.
                isActive
                  ? 'bg-red-tint text-red-dark'
                  : 'text-nav-idle hover:bg-soft hover:text-ink',
              )
            }
          >
            <Icon name={entry.icon} size={rail ? 18 : 16} className="shrink-0" />
            <span className={cn('min-w-0', rail && 'hyphens-auto')}>{t(entry.key)}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

export function NavRail() {
  const { t } = useLocale();

  return (
    <aside className="sticky top-0 hidden h-screen w-rail shrink-0 overflow-y-auto border-r border-line bg-white px-4 pb-8 pt-5 md:block">
      <div className="mb-6">
        <Brand />
      </div>

      <nav aria-label={t('nav.menu')}>
        <NavList layout="rail" />
      </nav>

      <div className="mt-6 flex flex-col gap-4 border-t border-line pt-5">
        <button
          type="button"
          className="flex items-center justify-center gap-2 rounded-md bg-red py-3 text-sm font-bold text-white transition hover:bg-red-dark"
        >
          <span className="h-1.5 w-1.5 animate-blink rounded-full bg-white" aria-hidden="true" />
          {t('action.liveTv')}
        </button>

        <ul className="flex justify-center gap-3.5 text-nav-idle">
          {SOCIALS.map((social) => (
            <li key={social.name}>
              <a
                href={social.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={social.label}
                className="block transition hover:text-red"
              >
                <Icon name={social.name} size={15} />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

/**
 * The mobile drawer. Escape closes it, focus moves into it on open, the page behind it
 * does not scroll, and it closes itself on navigation — a drawer that survives a route
 * change hides the page the reader just asked for.
 */
export function NavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useLocale();
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // The same three overlay behaviours the dialog uses, from the same place.
  useEscapeKey(open, onClose);
  useBodyScrollLock(open);
  useFocusTrap(panelRef, open);

  useEffect(() => {
    closeRef.current();
  }, [location.pathname]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.menu')}
        className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col gap-5 overflow-y-auto bg-white px-4 py-5 shadow-card"
      >
        <div className="flex items-start justify-between">
          <Brand compact />
          <button
            type="button"
            onClick={onClose}
            aria-label={t('nav.close')}
            className="rounded-md p-1 text-muted transition hover:text-red"
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <nav aria-label={t('nav.menu')}>
          <NavList layout="drawer" onNavigate={onClose} />
        </nav>
      </div>
    </div>
  );
}
